/**
 * IndexedDB Database Setup with Dexie.js
 *
 * This is the foundation for all local data storage.
 * All data is stored locally first (offline-first).
 * Sync to server happens in background when online.
 */

import Dexie, { Table } from 'dexie';
import { generateId } from '@utils/id';

// ============================================================
// Type Definitions
// ============================================================

export type AccountType = 'checking' | 'savings' | 'cash' | 'credit' | 'crypto' | 'investment';
export type TransactionType = 'income' | 'expense' | 'transfer';
export type CategoryType = 'income' | 'expense';
export type BudgetPeriod = 'weekly' | 'monthly' | 'yearly';
export type SyncStatus = 'synced' | 'pending' | 'conflict';
export type ScheduleFrequency = 'weekly' | 'biweekly' | 'monthly' | 'yearly';

export interface Account {
  id?: string;
  name: string;             // Short nickname: "C", "Rev", "C-app"
  fullName?: string;        // Full name: "Chase", "Revolut", "Cash App"
  type: AccountType;
  currency: string;         // ISO 4217: "USD", "THB", "EUR"
  balance: number;          // Current balance (computed from transactions)
  initialBalance?: number;  // BUG-058 FIX: Starting balance not derived from transactions (e.g., from import)
  isActive: boolean;        // Show/hide in UI
  sortOrder?: number;       // User-defined display order (lower = first)
  createdAt: Date;
  updatedAt: Date;
  syncStatus: SyncStatus;
}

export interface Transaction {
  id?: string;
  accountId: string;        // Source account
  type: TransactionType;
  amount: number;           // Always positive
  categoryId?: string;      // For income/expense (required)
  toAccountId?: string;     // For transfers (destination account)
  description: string;      // User notes
  date: Date;               // Transaction date
  tags?: string[];          // Optional tags for filtering
  currency: string;         // Transaction currency
  createdAt: Date;
  updatedAt: Date;
  syncStatus: SyncStatus;
  deletedAt?: Date;         // Soft delete timestamp (for sync)
  // Scheduled transaction link (Phase 1)
  scheduledTransactionId?: string;  // Links to schedule that created it
  wasSkipped?: boolean;             // True if user skipped this occurrence
  // Import deduplication (Phase 2 - Universal Import)
  importFingerprint?: string;       // Hash for duplicate detection (date+amount+desc)
  importFitId?: string;             // OFX FITID for bank transaction ID
  importSource?: string;            // Source identifier (e.g., 'chase-ofx', 'revolut-csv')
}

export interface Category {
  id?: string;
  name: string;             // Display name
  type: CategoryType;       // income or expense
  icon?: string;            // Emoji or icon name
  color?: string;           // Hex color for charts
  parentId?: string;        // For subcategories
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Budget {
  id?: string;
  categoryId: string;       // Which category to budget
  amount: number;           // Budget limit
  period: BudgetPeriod;
  startDate: Date;          // When budget period starts
  isActive: boolean;
}

// ============================================================
// Scheduled Transactions (Auto-detected recurring bills/income)
// ============================================================

export interface ScheduledTransaction {
  id: string;
  accountId: string;
  categoryId?: string;
  toAccountId?: string;           // For transfers
  type: TransactionType;
  amount: number;
  description: string;

  // Schedule configuration
  frequency: ScheduleFrequency;
  dayOfMonth?: number;            // 1-31 for monthly/yearly
  dayOfWeek?: number;             // 0-6 for weekly/biweekly
  monthOfYear?: number;           // 1-12 for yearly

  // State tracking
  nextDueDate: Date;
  lastGeneratedDate?: Date;       // Last date we auto-created for
  isPaused: boolean;
  pausedUntil?: Date;             // For "ignore 1 month" - resumes after this
  isIgnoredPermanently: boolean;  // Never auto-detect again
  endDate?: Date;                 // Optional end date

  // Source tracking
  autoDetected: boolean;          // true = system detected, false = user created
  detectionKey?: string;          // Payee/description used for detection matching
  dismissCount: number;           // Track how many times dismissed

  // Metadata
  createdAt: Date;
  updatedAt: Date;
  syncStatus: SyncStatus;
  deletedAt?: Date;               // Soft delete for sync
}

export interface SkippedOccurrence {
  id: string;
  scheduledTransactionId: string;
  skippedDate: Date;              // The due date that was skipped
  reason?: string;                // Optional user note
  createdAt: Date;
}

// ============================================================
// Holdings Adjustments (Crypto page - manual gains/losses/value syncs)
// ============================================================

export type HoldingsAdjustmentKind = 'value_sync' | 'gain' | 'loss' | 'correction';

export interface HoldingsAdjustment {
  id: string;
  groupId: string;                // Watch group this belongs to (e.g. 'default-crypto')
  date: Date;                     // When the adjustment was recorded
  amount: number;                 // Signed: positive increases tracked value
  kind: HoldingsAdjustmentKind;
  note?: string;
  createdAt: Date;
  updatedAt: Date;
  syncStatus: SyncStatus;
  deletedAt?: Date;               // Soft delete for sync
}

// ============================================================
// Database Class
// ============================================================

export class NinjaCashDB extends Dexie {
  accounts!: Table<Account>;
  transactions!: Table<Transaction>;
  categories!: Table<Category>;
  budgets!: Table<Budget>;
  scheduledTransactions!: Table<ScheduledTransaction>;
  skippedOccurrences!: Table<SkippedOccurrence>;
  holdingsAdjustments!: Table<HoldingsAdjustment>;

  constructor() {
    super('ninjacash');

    // Schema version 2 - added toAccountId index for transfer queries
    this.version(2).stores({
      // Primary key: id (auto-generated if not provided)
      // Indexed fields after comma for fast queries
      accounts: 'id, name, type, isActive, syncStatus',
      transactions: 'id, accountId, type, categoryId, toAccountId, date, syncStatus, [accountId+date]',
      categories: 'id, name, type, parentId, isActive',
      budgets: 'id, categoryId, period, isActive',
    });

    // Schema version 3 - added sortOrder for drag-and-drop account reordering
    this.version(3).stores({
      accounts: 'id, name, type, isActive, syncStatus, sortOrder',
      transactions: 'id, accountId, type, categoryId, toAccountId, date, syncStatus, [accountId+date]',
      categories: 'id, name, type, parentId, isActive',
      budgets: 'id, categoryId, period, isActive',
    });

    // Schema version 4 - added scheduled transactions and skipped occurrences
    this.version(4).stores({
      accounts: 'id, name, type, isActive, syncStatus, sortOrder',
      transactions: 'id, accountId, type, categoryId, toAccountId, date, syncStatus, scheduledTransactionId, [accountId+date]',
      categories: 'id, name, type, parentId, isActive',
      budgets: 'id, categoryId, period, isActive',
      scheduledTransactions: 'id, accountId, type, nextDueDate, isPaused, isIgnoredPermanently, detectionKey, syncStatus',
      skippedOccurrences: 'id, scheduledTransactionId, skippedDate, [scheduledTransactionId+skippedDate]',
    });

    // Schema version 5 - force table recreation after fixing compound index
    this.version(5).stores({
      accounts: 'id, name, type, isActive, syncStatus, sortOrder',
      transactions: 'id, accountId, type, categoryId, toAccountId, date, syncStatus, scheduledTransactionId, [accountId+date]',
      categories: 'id, name, type, parentId, isActive',
      budgets: 'id, categoryId, period, isActive',
      scheduledTransactions: 'id, accountId, type, nextDueDate, isPaused, isIgnoredPermanently, detectionKey, syncStatus',
      skippedOccurrences: 'id, scheduledTransactionId, skippedDate, [scheduledTransactionId+skippedDate]',
    });

    // Schema version 6 - Add import fingerprint fields for duplicate detection
    this.version(6).stores({
      accounts: 'id, name, type, isActive, syncStatus, sortOrder',
      transactions: 'id, accountId, type, categoryId, toAccountId, date, syncStatus, scheduledTransactionId, importFingerprint, importFitId, [accountId+date]',
      categories: 'id, name, type, parentId, isActive',
      budgets: 'id, categoryId, period, isActive',
      scheduledTransactions: 'id, accountId, type, nextDueDate, isPaused, isIgnoredPermanently, detectionKey, syncStatus',
      skippedOccurrences: 'id, scheduledTransactionId, skippedDate, [scheduledTransactionId+skippedDate]',
    });

    // Schema version 7 - Add initialBalance field for imported/manual starting balances (BUG-058)
    // This preserves starting balances during reconciliation (prevents destroying imported balances)
    this.version(7).stores({
      accounts: 'id, name, type, isActive, syncStatus, sortOrder',
      transactions: 'id, accountId, type, categoryId, toAccountId, date, syncStatus, scheduledTransactionId, importFingerprint, importFitId, [accountId+date]',
      categories: 'id, name, type, parentId, isActive',
      budgets: 'id, categoryId, period, isActive',
      scheduledTransactions: 'id, accountId, type, nextDueDate, isPaused, isIgnoredPermanently, detectionKey, syncStatus',
      skippedOccurrences: 'id, scheduledTransactionId, skippedDate, [scheduledTransactionId+skippedDate]',
    });

    // Schema version 8 - holdings adjustments promoted from localStorage to a
    // synced Dexie table (crypto gains/losses/value syncs now cross-device)
    this.version(8).stores({
      accounts: 'id, name, type, isActive, syncStatus, sortOrder',
      transactions: 'id, accountId, type, categoryId, toAccountId, date, syncStatus, scheduledTransactionId, importFingerprint, importFitId, [accountId+date]',
      categories: 'id, name, type, parentId, isActive',
      budgets: 'id, categoryId, period, isActive',
      scheduledTransactions: 'id, accountId, type, nextDueDate, isPaused, isIgnoredPermanently, detectionKey, syncStatus',
      skippedOccurrences: 'id, scheduledTransactionId, skippedDate, [scheduledTransactionId+skippedDate]',
      holdingsAdjustments: 'id, groupId, syncStatus',
    });
  }
}

// Singleton instance
export const db = new NinjaCashDB();

// ============================================================
// Initialization & Utilities
// ============================================================

const DEBUG = import.meta.env.VITE_DEBUG === 'true' ||
  (typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('debug'));

function log(...args: unknown[]): void {
  if (DEBUG) {
    console.log('[DB]', ...args);
  }
}

/**
 * Initialize the database.
 * Call this once on app startup.
 */
export async function initDB(): Promise<boolean> {
  try {
    await db.open();
    log('IndexedDB initialized successfully');

    const stats = await getDBStats();
    log('Current data:', stats);

    return true;
  } catch (error) {
    console.error('[DB] Failed to initialize:', error);
    return false;
  }
}

/**
 * Get database statistics.
 * Returns 0 for tables that don't exist yet (schema not upgraded).
 */
export async function getDBStats(): Promise<{
  accounts: number;
  transactions: number;
  categories: number;
  budgets: number;
  scheduledTransactions: number;
  skippedOccurrences: number;
  holdingsAdjustments: number;
}> {
  // Helper to safely count a table (returns 0 if table doesn't exist)
  async function safeCount(table: Table<unknown> | undefined): Promise<number> {
    if (!table) return 0;
    try {
      return await table.count();
    } catch (err) {
      if (err instanceof Error && err.name === 'NotFoundError') {
        return 0;
      }
      throw err;
    }
  }

  return {
    accounts: await safeCount(db.accounts),
    transactions: await safeCount(db.transactions),
    categories: await safeCount(db.categories),
    budgets: await safeCount(db.budgets),
    scheduledTransactions: await safeCount(db.scheduledTransactions),
    skippedOccurrences: await safeCount(db.skippedOccurrences),
    holdingsAdjustments: await safeCount(db.holdingsAdjustments),
  };
}

/**
 * Clear all data from the database.
 * Use with caution - this is destructive!
 */
export async function clearAllData(): Promise<void> {
  log('Clearing all data...');
  await db.transaction('rw', [db.accounts, db.transactions, db.categories, db.budgets, db.scheduledTransactions, db.skippedOccurrences, db.holdingsAdjustments], async () => {
    await db.accounts.clear();
    await db.transactions.clear();
    await db.categories.clear();
    await db.budgets.clear();
    await db.scheduledTransactions.clear();
    await db.skippedOccurrences.clear();
    await db.holdingsAdjustments.clear();
  });
  log('All data cleared');
}

/**
 * Export all data as JSON.
 * Useful for backup or migration.
 */
export async function exportData(): Promise<{
  accounts: Account[];
  transactions: Transaction[];
  categories: Category[];
  budgets: Budget[];
  scheduledTransactions: ScheduledTransaction[];
  skippedOccurrences: SkippedOccurrence[];
  holdingsAdjustments: HoldingsAdjustment[];
  exportedAt: string;
}> {
  return {
    accounts: await db.accounts.toArray(),
    transactions: await db.transactions.toArray(),
    categories: await db.categories.toArray(),
    budgets: await db.budgets.toArray(),
    scheduledTransactions: await db.scheduledTransactions.toArray(),
    skippedOccurrences: await db.skippedOccurrences.toArray(),
    holdingsAdjustments: await db.holdingsAdjustments.toArray(),
    exportedAt: new Date().toISOString(),
  };
}

// ============================================================
// Default Categories (seed data)
// ============================================================

export const DEFAULT_EXPENSE_CATEGORIES = [
  { name: 'Food', icon: '🍔', color: '#FF6B6B' },
  { name: 'Transport', icon: '🚗', color: '#4ECDC4' },
  { name: 'Shopping', icon: '🛒', color: '#45B7D1' },
  { name: 'Bills', icon: '📄', color: '#96CEB4' },
  { name: 'Entertainment', icon: '🎮', color: '#DDA0DD' },
  { name: 'Health', icon: '💊', color: '#98D8C8' },
  { name: 'Education', icon: '📚', color: '#F7DC6F' },
  { name: 'Other', icon: '📦', color: '#AEB6BF' },
];

export const DEFAULT_INCOME_CATEGORIES = [
  { name: 'Salary', color: '#2ECC71' },
  { name: 'Freelance', color: '#27AE60' },
  { name: 'Investment', color: '#1ABC9C' },
  { name: 'Gift', color: '#16A085' },
  { name: 'Other', color: '#82E0AA' },
];

/**
 * Seed default categories if none exist.
 */
export async function seedDefaultCategories(): Promise<void> {
  const count = await db.categories.count();
  if (count > 0) {
    log('Categories already exist, skipping seed');
    return;
  }

  log('Seeding default categories...');

  const categories: Omit<Category, 'id'>[] = [
    ...DEFAULT_EXPENSE_CATEGORIES.map((c) => ({
      name: c.name,
      type: 'expense' as const,
      icon: c.icon,
      color: c.color,
      isActive: true,
    })),
    ...DEFAULT_INCOME_CATEGORIES.map((c) => ({
      name: c.name,
      type: 'income' as const,
      icon: (c as { icon?: string }).icon,
      color: c.color,
      isActive: true,
    })),
  ];

  await db.categories.bulkAdd(
    categories.map((c) => ({
      ...c,
      id: generateId(),
    }))
  );

  log('Seeded', categories.length, 'default categories');
}

// ============================================================
// Mock Data for Testing (matches design mockup)
// ============================================================

/**
 * Seed mock accounts and transactions for testing.
 * This creates data similar to the design mockup (nc_v1.png).
 */
export async function seedMockData(): Promise<void> {
  const accountCount = await db.accounts.count();
  if (accountCount > 0) {
    log('Accounts already exist, skipping mock data');
    return;
  }

  log('Seeding mock data...');

  // Ensure categories exist
  await seedDefaultCategories();
  const categories = await db.categories.toArray();
  const salaryCategory = categories.find(c => c.name === 'Salary');
  const foodCategory = categories.find(c => c.name === 'Food');
  const transportCategory = categories.find(c => c.name === 'Transport');
  const shoppingCategory = categories.find(c => c.name === 'Shopping');
  const billsCategory = categories.find(c => c.name === 'Bills');
  const entertainmentCategory = categories.find(c => c.name === 'Entertainment');

  const now = new Date();

  // Mock accounts matching the design mockup
  const mockAccounts: Omit<Account, 'id'>[] = [
    { name: 'C', fullName: 'Chase Checking', type: 'checking', currency: 'USD', balance: 2520.00, isActive: true, createdAt: now, updatedAt: now, syncStatus: 'synced' },
    { name: 'Rev', fullName: 'Revolut', type: 'checking', currency: 'USD', balance: 810.00, isActive: true, createdAt: now, updatedAt: now, syncStatus: 'synced' },
    { name: 's Rev s', fullName: 'Revolut Savings', type: 'savings', currency: 'USD', balance: 1219.00, isActive: true, createdAt: now, updatedAt: now, syncStatus: 'synced' },
    { name: 'C-app', fullName: 'Cash App', type: 'cash', currency: 'USD', balance: 130.00, isActive: true, createdAt: now, updatedAt: now, syncStatus: 'synced' },
    { name: 's C app s', fullName: 'Cash App Savings', type: 'savings', currency: 'USD', balance: 253.00, isActive: true, createdAt: now, updatedAt: now, syncStatus: 'synced' },
    { name: 'paypal', fullName: 'PayPal', type: 'checking', currency: 'USD', balance: 168.00, isActive: true, createdAt: now, updatedAt: now, syncStatus: 'synced' },
    { name: 'crypto com', fullName: 'Crypto.com', type: 'checking', currency: 'USD', balance: 21.00, isActive: true, createdAt: now, updatedAt: now, syncStatus: 'synced' },
    { name: 'baht', fullName: 'Thai Baht', type: 'cash', currency: 'THB', balance: 0, isActive: true, createdAt: now, updatedAt: now, syncStatus: 'synced' },
    { name: 'pending', fullName: 'Pending', type: 'checking', currency: 'USD', balance: 0, isActive: true, createdAt: now, updatedAt: now, syncStatus: 'synced' },
  ];

  // Add accounts and get their IDs
  const accountIds: string[] = [];
  for (const account of mockAccounts) {
    const id = generateId();
    await db.accounts.add({ ...account, id });
    accountIds.push(id);
  }

  // Get the first account (Chase) for transactions
  const chaseId = accountIds[0];
  const revolutId = accountIds[1];

  // Mock transactions for this month (to show income/expense totals)
  const thisMonth = new Date();
  const lastMonth = new Date(thisMonth.getFullYear(), thisMonth.getMonth() - 1, 15);

  const mockTransactions: Omit<Transaction, 'id'>[] = [
    // Income this month
    { accountId: chaseId, type: 'income', amount: 5500, categoryId: salaryCategory?.id, description: 'Monthly Salary', date: new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 1), currency: 'USD', createdAt: now, updatedAt: now, syncStatus: 'synced' },
    { accountId: revolutId, type: 'income', amount: 327, categoryId: salaryCategory?.id, description: 'Freelance work', date: new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 10), currency: 'USD', createdAt: now, updatedAt: now, syncStatus: 'synced' },

    // Expenses this month
    { accountId: chaseId, type: 'expense', amount: 1200, categoryId: billsCategory?.id, description: 'Rent', date: new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 1), currency: 'USD', createdAt: now, updatedAt: now, syncStatus: 'synced' },
    { accountId: chaseId, type: 'expense', amount: 450, categoryId: foodCategory?.id, description: 'Groceries', date: new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 5), currency: 'USD', createdAt: now, updatedAt: now, syncStatus: 'synced' },
    { accountId: chaseId, type: 'expense', amount: 120, categoryId: transportCategory?.id, description: 'Gas', date: new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 8), currency: 'USD', createdAt: now, updatedAt: now, syncStatus: 'synced' },
    { accountId: revolutId, type: 'expense', amount: 85, categoryId: entertainmentCategory?.id, description: 'Netflix & Spotify', date: new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 3), currency: 'USD', createdAt: now, updatedAt: now, syncStatus: 'synced' },
    { accountId: chaseId, type: 'expense', amount: 230, categoryId: shoppingCategory?.id, description: 'Amazon order', date: new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 12), currency: 'USD', createdAt: now, updatedAt: now, syncStatus: 'synced' },
    { accountId: chaseId, type: 'expense', amount: 2800, categoryId: billsCategory?.id, description: 'Credit card payment', date: new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 15), currency: 'USD', createdAt: now, updatedAt: now, syncStatus: 'synced' },
    { accountId: revolutId, type: 'expense', amount: 600, categoryId: shoppingCategory?.id, description: 'New laptop', date: new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 18), currency: 'USD', createdAt: now, updatedAt: now, syncStatus: 'synced' },

    // Some last month transactions for history
    { accountId: chaseId, type: 'income', amount: 5500, categoryId: salaryCategory?.id, description: 'Monthly Salary', date: lastMonth, currency: 'USD', createdAt: now, updatedAt: now, syncStatus: 'synced' },
    { accountId: chaseId, type: 'expense', amount: 1200, categoryId: billsCategory?.id, description: 'Rent', date: lastMonth, currency: 'USD', createdAt: now, updatedAt: now, syncStatus: 'synced' },
  ];

  for (const transaction of mockTransactions) {
    await db.transactions.add({ ...transaction, id: generateId() });
  }

  log('Seeded', mockAccounts.length, 'accounts and', mockTransactions.length, 'transactions');
}

/**
 * Clear and reseed mock data.
 * Useful for resetting to a known state.
 */
export async function resetToMockData(): Promise<void> {
  await clearAllData();
  await seedMockData();
}
